import React, { useEffect, useMemo, useState } from "react";
import axios from "../import/axiosConfig";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  PurchaseProductList_Product,
  ViewOne_Product,
} from "../import/ApiEndPoint/Api";

const CustomerTargetEditPage = () => {
  const [fy, setFy] = useState(getCurrentFYLabel());
  const [month, setMonth] = useState(getCurrentMonthName());
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [productList, setProductList] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(null);
  const [incrementPer, setIncrementPer] = useState("");
  const [locked, setLocked] = useState(false);
  const [productSizeMap, setProductSizeMap] = useState({});

  const history = useNavigate();
  const location = useLocation();

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const round2 = (v) => Number(safeNum(v).toFixed(2));

  const FY_MONTHS = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const CALENDAR_MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  function getCurrentFYLabel() {
    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth();
    const startYear = monthIndex >= 3 ? year : year - 1;
    return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
  }

  function getCurrentMonthName() {
    return CALENDAR_MONTHS[new Date().getMonth()];
  }

  const getFYStartYear = (fyValue) => {
    const year = Number(String(fyValue || "").split("-")[0]);
    return Number.isFinite(year) ? year : 0;
  };

  const isPastMonthLockedFrontend = (fyValue, monthName) => {
    const targetFyStart = getFYStartYear(fyValue);
    const currentFyStart = getFYStartYear(getCurrentFYLabel());
    const targetMonthIndex = FY_MONTHS.indexOf(String(monthName || "").trim());
    const currentMonthIndex = FY_MONTHS.indexOf(getCurrentMonthName());

    if (!targetFyStart || targetMonthIndex === -1) return true;
    if (targetFyStart < currentFyStart) return true;
    if (targetFyStart > currentFyStart) return false;

    return targetMonthIndex < currentMonthIndex;
  };

  const getEditablePeriod = () => {
    const selectedFy = fy || getCurrentFYLabel();
    const selectedMonth = month || getCurrentMonthName();
    const mustShiftToCurrent =
      locked || isPastMonthLockedFrontend(selectedFy, selectedMonth);

    if (mustShiftToCurrent) {
      return {
        fyear: getCurrentFYLabel(),
        month: getCurrentMonthName(),
        shifted: true,
      };
    }

    return {
      fyear: selectedFy,
      month: selectedMonth,
      shifted: false,
    };
  };

  const recalcCustomerTotal = (products = []) => {
    return products.reduce((sum, p) => sum + safeNum(p.total), 0);
  };

  const getStoredSecondarySize = (product) => {
    return (
      safeNum(product?.secondarySize) ||
      safeNum(product?.secondarysize) ||
      safeNum(product?.SecondarySize) ||
      safeNum(product?.secondaryQty) ||
      safeNum(product?.SecondaryQty) ||
      0
    );
  };

  const getProductSecondarySize = (productId, productObj = {}) => {
    return (
      safeNum(productSizeMap[productId]) ||
      getStoredSecondarySize(productObj) ||
      1
    );
  };

  const fetchProductSecondarySize = async (productId) => {
    try {
      if (!productId) return 1;

      if (productSizeMap[productId]) {
        return safeNum(productSizeMap[productId]) || 1;
      }

      const res = await axios.get(`${ViewOne_Product}${productId}`);
      const payload = res?.data ?? res;

      const product =
        payload?.Product ||
        payload?.product ||
        payload?.data ||
        payload?.Data ||
        payload;

      const secondarySize =
        safeNum(product?.secondarySize) ||
        safeNum(product?.secondarysize) ||
        safeNum(product?.SecondarySize) ||
        safeNum(product?.secondaryQty) ||
        safeNum(product?.SecondaryQty) ||
        1;

      setProductSizeMap((prev) => ({
        ...prev,
        [productId]: secondarySize || 1,
      }));

      return secondarySize || 1;
    } catch (err) {
      console.log("secondarySize fetch error", err);
      return 1;
    }
  };

  useEffect(() => {
    if (!location.state) return;

    const customerData = location.state.customerData || {};
    const safeProducts = Array.isArray(customerData.products)
      ? customerData.products.map((p) => {
          const secondarySize = getStoredSecondarySize(p) || 1;

          let pQty = safeNum(p.pQty);
          let sQty =
            p.sQty !== undefined && p.sQty !== null && p.sQty !== ""
              ? safeNum(p.sQty)
              : secondarySize
                ? round2(pQty / secondarySize)
                : 0;

          if (!pQty && sQty && secondarySize) {
            pQty = round2(sQty * secondarySize);
          }

          const price = safeNum(p.price);
          const total =
            p.total !== undefined && p.total !== null && p.total !== ""
              ? safeNum(p.total)
              : round2(pQty * price);

          return {
            ...p,
            category: p.category || p.catageory || "",
            subCategory: p.subCategory || p.SubCategory || "",
            productName: p.productName || p.name || "",
            secondarySize,
            pQty,
            sQty,
            price,
            total,
          };
        })
      : [];

    const initialMap = {};
    safeProducts.forEach((p) => {
      if (p?.productId && safeNum(p?.secondarySize)) {
        initialMap[p.productId] = safeNum(p.secondarySize);
      }
    });

    setProductSizeMap(initialMap);
    const nextFy = location.state.fy || getCurrentFYLabel();
    const nextMonth = location.state.month || getCurrentMonthName();
    setFy(nextFy);
    setMonth(nextMonth);
    setIncrementPer(location.state.incrementper ?? "");
    setLocked(
      Boolean(location.state.locked) ||
        isPastMonthLockedFrontend(nextFy, nextMonth),
    );
    setCustomers([
      {
        ...customerData,
        total: recalcCustomerTotal(safeProducts),
        products: safeProducts,
      },
    ]);
  }, [location.state]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        const database = user?.database || "";
        const res = await axios.get(
          `${PurchaseProductList_Product}${database}`,
        );
        const payload = res?.data ?? res;
        const list = Array.isArray(payload?.Product) ? payload.Product : [];
        setProductList(list);
      } catch (err) {
        console.log(err);
        setProductList([]);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const preloadSecondarySizes = async () => {
      const ids = [];

      customers.forEach((c) => {
        (c.products || []).forEach((p) => {
          if (p?.productId && !productSizeMap[p.productId]) {
            ids.push(p.productId);
          }
        });
      });

      const uniqueIds = [...new Set(ids)];
      if (!uniqueIds.length) return;

      for (const id of uniqueIds) {
        await fetchProductSecondarySize(id);
      }
    };

    preloadSecondarySizes();
  }, [customers]);

  const pageTotal = useMemo(() => {
    return customers.reduce((sum, c) => sum + safeNum(c.total), 0);
  }, [customers]);

  const handleChange = async (cIndex, pIndex, field, value) => {
    const updated = [...customers];
    const currentCustomer = { ...updated[cIndex] };
    const products = [...(currentCustomer.products || [])];
    const product = { ...products[pIndex] };

    const productId = product.productId;
    const conversion = await fetchProductSecondarySize(productId);

    let pQty = safeNum(product.pQty);
    let sQty = safeNum(product.sQty);
    let price = safeNum(product.price);

    if (field === "sQty") {
      sQty = safeNum(value);
      pQty = round2(sQty * conversion);
    } else if (field === "pQty") {
      pQty = safeNum(value);
      sQty = conversion ? round2(pQty / conversion) : 0;
    } else if (field === "price") {
      price = safeNum(value);
    }

    product.secondarySize = conversion || 1;
    product.pQty = pQty;
    product.sQty = sQty;
    product.price = price;
    product.total = round2(pQty * price);

    products[pIndex] = product;
    currentCustomer.products = products;
    currentCustomer.total = recalcCustomerTotal(products);
    updated[cIndex] = currentCustomer;

    setCustomers(updated);
  };

  const removeProduct = (cIndex, pIndex) => {
    const updated = [...customers];
    const customer = { ...updated[cIndex] };
    const products = [...(customer.products || [])];

    products.splice(pIndex, 1);

    customer.products = products;
    customer.total = recalcCustomerTotal(products);
    updated[cIndex] = customer;

    setCustomers(updated);
  };

  const updateCustomer = async (customer) => {
    try {
      setSavingId(customer.userId);
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const editablePeriod = getEditablePeriod();

      const payload = {
        database: userData.database,
        fyear: editablePeriod.fyear,
        month: editablePeriod.month,
        incrementper: Number(incrementPer || 0),
        customerId:
          customer.userId ||
          customer.customerId ||
          customer._id ||
          location.state?.customerId ||
          "",
        created_by: userData._id,
        productItem: customer.products.map((p) => {
          const primaryQty = Number(p.pQty || 0);
          const secondaryQty = Number(p.sQty || 0);
          const price = Number(p.price || 0);
          const total = Number(p.total || 0);

          return {
            productId: p.productId,
            ProductId: p.productId,
            product_id: p.productId,

            category: p.category || "",
            catageory: p.category || "",
            subCategory: p.subCategory || "",
            SubCategory: p.subCategory || "",
            productName: p.productName || "",
            Product_Title: p.productName || "",

            pQty: primaryQty,
            primaryQty,
            qtyAssign: primaryQty,
            targetQty: primaryQty,

            sQty: secondaryQty,
            secondaryQty,
            secondarySize: Number(getProductSecondarySize(p.productId, p)),

            price,
            rate: price,

            total,
            totalPrice: total,
            target: total,
            amount: total,
            targetAmount: total,
            monthlyTarget: total,
            productTarget: total,
            currentTarget: total,
            newTarget: total,

            targetStatus: "updated",
            saveStatus: "updated",
            isEdited: true,
            isUpdated: true,
          };
        }),
      };

      if (!payload.database || !payload.fyear || !payload.month) {
        throw new Error("Database, financial year, or month is missing");
      }

      if (!payload.customerId) {
        throw new Error("Customer id is missing");
      }

      if (!payload.productItem.length) {
        throw new Error("Please add at least one product");
      }

      await axios.put("/target/customer-target", payload);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: editablePeriod.shifted
          ? `Completed month is locked. Target updated from ${editablePeriod.month} ${editablePeriod.fyear} and upper hierarchy will be recalculated.`
          : "Customer target updated successfully and upper hierarchy will be recalculated.",
        confirmButtonColor: "#2563eb",
      });

      history("/app/target-tabs");
    } catch (err) {
      console.log(err);

      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong";

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: errorMessage,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setSavingId(null);
    }
  };

  const addMoreProduct = (cIndex) => {
    setShowProductSelector(cIndex);
  };

  const handleProductSelect = async (cIndex, productId) => {
    const selected = productList.find((p) => p._id === productId);
    if (!selected) return;

    const updated = [...customers];
    const currentCustomer = { ...updated[cIndex] };
    const products = [...(currentCustomer.products || [])];

    const exists = products.some((p) => p.productId === productId);
    if (exists) {
      Swal.fire({
        icon: "warning",
        title: "Already Added",
        text: "Product already added",
        confirmButtonColor: "#f59e0b",
      });
      return;
    }

    const secondarySize = await fetchProductSecondarySize(productId);

    products.push({
      productId: selected._id,
      productName: selected.Product_Title || "",
      category:
        selected.category || selected.Category || selected.catageory || "",
      subCategory:
        selected.SubCategory ||
        selected.subCategory ||
        selected.subcategory ||
        "",
      secondarySize,
      pQty: 0,
      sQty: 0,
      price: safeNum(
        selected.SalesRate ||
          selected.Product_MRP ||
          selected.rate ||
          selected.Rate ||
          0,
      ),
      total: 0,
    });

    currentCustomer.products = products;
    currentCustomer.total = recalcCustomerTotal(products);
    updated[cIndex] = currentCustomer;

    setCustomers(updated);
    setShowProductSelector(null);
  };

  const editablePeriod = getEditablePeriod();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "18px",
            boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "18px 20px 12px",
              borderBottom: "1px solid #eef2f7",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: 900,
                    color: "#0f172a",
                  }}
                >
                  Customer Target Edit
                </h2>
                <p
                  style={{
                    margin: "6px 0 0 0",
                    color: "#64748b",
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  Secondary Qty ↔ Primary Qty conversion is fetched product-wise
                  from ViewOne_Product using product _id. If the selected month
                  is completed, update will automatically start from the current
                  month.
                </p>
              </div>

              <button
                onClick={() => history("/app/target-tabs")}
                style={{
                  padding: "10px 16px",
                  background: "#e2e8f0",
                  color: "#0f172a",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontWeight: 800,
                }}
              >
                ← Back
              </button>
            </div>
          </div>

          <div
            style={{
              padding: "16px 20px 20px",
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              alignItems: "end",
            }}
          >
            <div style={fieldWrap}>
              <label style={labelStyle}>Financial Year</label>
              <div style={statBox}>{fy}</div>
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Month</label>
              <div style={statBox}>{month}</div>
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Save From FY</label>
              <div
                style={{
                  ...statBox,
                  background: editablePeriod.shifted ? "#fff7ed" : "#f0fdf4",
                  borderColor: editablePeriod.shifted ? "#fed7aa" : "#bbf7d0",
                  fontWeight: 900,
                }}
              >
                {editablePeriod.fyear}
              </div>
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Save From Month</label>
              <div
                style={{
                  ...statBox,
                  background: editablePeriod.shifted ? "#fff7ed" : "#f0fdf4",
                  borderColor: editablePeriod.shifted ? "#fed7aa" : "#bbf7d0",
                  fontWeight: 900,
                }}
              >
                {editablePeriod.month}
              </div>
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Increment %</label>
              <input
                type="number"
                value={incrementPer}
                disabled={false}
                onChange={(e) => setIncrementPer(e.target.value)}
                style={topInput}
              />
            </div>

            <div style={fieldWrap}>
              <label style={labelStyle}>Page Total</label>
              <div
                style={{
                  ...statBox,
                  background: "linear-gradient(180deg,#eff6ff 0%,#e0ecff 100%)",
                  borderColor: "#c8dcff",
                  fontWeight: 900,
                }}
              >
                ₹ {Number(pageTotal || 0).toFixed(2)}
              </div>
            </div>

            {locked ? (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "999px",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  color: "#9a3412",
                  fontSize: "12px",
                  fontWeight: 800,
                }}
              >
                Completed month is locked. Editing will be saved from current
                month automatically
              </div>
            ) : null}
          </div>
        </div>

        {loading && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "18px",
              padding: "24px",
              textAlign: "center",
              fontWeight: 700,
              color: "#475569",
            }}
          >
            Loading...
          </div>
        )}

        {customers.map((customer, cIndex) => (
          <div
            key={customer.userId}
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "18px",
              boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "18px 20px 12px",
                borderBottom: "1px solid #eef2f7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: 900,
                    color: "#0f172a",
                  }}
                >
                  {customer.firstName}
                </h3>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    color: "#64748b",
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  Customer Total: ₹ {Number(customer.total || 0).toFixed(2)}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => addMoreProduct(cIndex)}
                  disabled={false}
                  style={{
                    padding: "10px 16px",
                    background: "#16a34a",
                    color: "#fff",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: 800,
                  }}
                >
                  + Add More
                </button>

                <button
                  onClick={() => updateCustomer(customer)}
                  disabled={savingId === customer.userId}
                  style={{
                    padding: "10px 16px",
                    background:
                      savingId === customer.userId ? "#93c5fd" : "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: "12px",
                    cursor:
                      savingId === customer.userId ? "not-allowed" : "pointer",
                    fontWeight: 800,
                  }}
                >
                  {savingId === customer.userId
                    ? "Updating..."
                    : "Update Customer"}
                </button>
              </div>
            </div>

            {showProductSelector === cIndex && (
              <div style={{ padding: "14px 20px 0" }}>
                <select
                  style={{ ...topInput, width: "260px" }}
                  onChange={(e) => handleProductSelect(cIndex, e.target.value)}
                  defaultValue=""
                >
                  <option value="">Select Product</option>
                  {productList.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.Product_Title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ padding: "16px 20px 20px" }}>
              <div
                style={{
                  overflowX: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: "16px",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    minWidth: "1120px",
                    borderCollapse: "collapse",
                    background: "#fff",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={th}>Product</th>
                      <th style={th}>Category</th>
                      <th style={th}>Sub Category</th>
                      <th style={th}>Secondary Size</th>
                      <th style={th}>Price</th>
                      <th style={th}>Secondary Qty</th>
                      <th style={th}>Primary Qty</th>
                      <th style={th}>Total</th>
                      <th style={th}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {customer.products.length === 0 ? (
                      <tr>
                        <td
                          colSpan="9"
                          style={{
                            padding: "18px",
                            textAlign: "center",
                            color: "#64748b",
                            fontWeight: 600,
                            borderTop: "1px solid #e5e7eb",
                          }}
                        >
                          No products added
                        </td>
                      </tr>
                    ) : (
                      customer.products.map((product, pIndex) => (
                        <tr key={`${product.productId}-${pIndex}`}>
                          <td style={tdStrong}>{product.productName}</td>
                          <td style={td}>{product.category || "-"}</td>
                          <td style={td}>{product.subCategory || "-"}</td>
                          <td style={td}>
                            {Number(
                              getProductSecondarySize(
                                product.productId,
                                product,
                              ),
                            )}
                          </td>

                          <td style={td}>
                            <input
                              type="number"
                              value={product.price}
                              disabled={false}
                              onChange={(e) =>
                                handleChange(
                                  cIndex,
                                  pIndex,
                                  "price",
                                  e.target.value,
                                )
                              }
                              style={cellInput}
                            />
                          </td>

                          <td style={td}>
                            <input
                              type="number"
                              value={product.sQty}
                              disabled={false}
                              onChange={(e) =>
                                handleChange(
                                  cIndex,
                                  pIndex,
                                  "sQty",
                                  e.target.value,
                                )
                              }
                              style={cellInput}
                            />
                          </td>

                          <td style={td}>
                            <input
                              type="number"
                              value={product.pQty}
                              disabled={false}
                              onChange={(e) =>
                                handleChange(
                                  cIndex,
                                  pIndex,
                                  "pQty",
                                  e.target.value,
                                )
                              }
                              style={cellInput}
                            />
                          </td>

                          <td style={tdMoney}>
                            ₹ {Number(product.total || 0).toFixed(2)}
                          </td>

                          <td style={td}>
                            <button
                              onClick={() => removeProduct(cIndex, pIndex)}
                              disabled={false}
                              style={{
                                padding: "8px 12px",
                                background: "#ef4444",
                                color: "#fff",
                                border: "none",
                                borderRadius: "10px",
                                cursor: "pointer",
                                fontWeight: 700,
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  marginTop: "14px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(180deg,#eff6ff 0%,#e0ecff 100%)",
                    border: "1px solid #c8dcff",
                    fontWeight: 900,
                    color: "#0f172a",
                  }}
                >
                  Customer Total: ₹ {Number(customer.total || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const fieldWrap = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  minWidth: "160px",
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 800,
  color: "#475569",
};

const statBox = {
  height: "40px",
  display: "flex",
  alignItems: "center",
  padding: "0 12px",
  borderRadius: "12px",
  border: "1px solid #dbe3ee",
  background: "#fff",
  color: "#0f172a",
  fontSize: "14px",
};

const topInput = {
  height: "40px",
  border: "1px solid #dbe3ee",
  borderRadius: "12px",
  padding: "0 12px",
  fontSize: "14px",
  outline: "none",
  background: "#fff",
};

const th = {
  padding: "12px 10px",
  borderBottom: "1px solid #e5e7eb",
  textAlign: "left",
  fontSize: "12px",
  color: "#334155",
  fontWeight: 800,
  whiteSpace: "nowrap",
};

const td = {
  padding: "10px",
  borderTop: "1px solid #eef2f7",
  fontSize: "13px",
  color: "#0f172a",
};

const tdStrong = {
  ...td,
  fontWeight: 800,
};

const tdMoney = {
  ...td,
  fontWeight: 900,
  color: "#0f172a",
};

const cellInput = {
  width: "100px",
  padding: "8px 10px",
  border: "1px solid #dbe3ee",
  borderRadius: "10px",
  fontSize: "13px",
  outline: "none",
};

export default CustomerTargetEditPage;
